/**
 * Turn Illustration Style Definitions
 * 
 * The "Turn Aesthetic" - a consistent visual language for all generated illustrations
 * inspired by hand-drawn children's book art with crayon/colored pencil textures.
 * 
 * Philosophy:
 * - Every scanned book becomes a "Turn book" with consistent, delightful illustrations
 * - Text is the hero; illustrations complement and enhance
 * - Warm, gentle, whimsical - never scary or overwhelming
 * - Accessibility: simple shapes, high contrast, clear subjects
 */

// ============================================================================
// Core Style Definition
// ============================================================================

/**
 * The canonical Turn illustration style prompt.
 * This is the foundation for all generated illustrations.
 * 
 * Design principles:
 * 1. Hand-drawn feel - not photorealistic, not 3D
 * 2. Crayon/colored pencil texture - visible strokes
 * 3. Warm, muted palette - gentle on young eyes
 * 4. Simple, rounded shapes - approachable characters
 * 5. Textured paper feel - adds warmth and authenticity
 */
export const TURN_STYLE_BASE = `
Hand-drawn children's book illustration with:
- Crayon and colored pencil texture with visible strokes
- Soft, rounded shapes with no sharp edges
- Warm, muted color palette (cream, soft yellow, dusty rose, sage green, sky blue)
- Simple, expressive characters with large eyes and gentle expressions
- Textured off-white paper background feel
- Gentle, whimsical mood that feels safe and inviting
- Soft shadows and gentle gradients
- Slightly imperfect lines (intentional wobble) for authentic hand-drawn feel
`.trim();

/**
 * Critical constraints for Turn illustrations.
 * These prevent common Gemini failure modes.
 */
export const TURN_STYLE_CONSTRAINTS = `
CRITICAL CONSTRAINTS:
- NO text, words, letters, numbers, signs, or labels anywhere in the image
- NO speech bubbles or thought bubbles
- NO realistic proportions - stylized and child-friendly
- NO dark, scary, or intense imagery
- NO sharp edges or angular shapes
- NO saturated neon colors
- NO 3D rendering or photorealistic elements
- NO busy or cluttered compositions
`.trim();

/**
 * Positive reinforcement for the style.
 * Placed at the end of prompts to reinforce key elements.
 */
export const TURN_STYLE_REINFORCEMENT = `
Remember: This is a purely visual illustration for a children's book.
- Warm and inviting atmosphere
- Clear focal point on the main subject
- Plenty of breathing room in the composition
- Hand-drawn crayon/pencil texture visible throughout
- Soft, gentle color palette
- Silent visual scene - no text elements of any kind
`.trim();

// ============================================================================
// Color Palette
// ============================================================================

/**
 * The Turn color palette - warm, muted, child-friendly colors.
 * Based on classic children's book illustration palettes.
 */
export const TURN_COLOR_PALETTE = {
  // Primary warm tones
  cream: '#FFF8E7',
  softYellow: '#F5E6A3',
  warmPeach: '#FFDAB9',
  dustyRose: '#D4A5A5',
  
  // Secondary cool tones (used sparingly)
  sageGreen: '#A8C090',
  skyBlue: '#A7C7E7',
  lavender: '#C5B9CD',
  
  // Neutrals
  warmGray: '#9E9E9E',
  softBrown: '#8B7355',
  charcoal: '#4A4A4A', // For outlines only
  
  // Background
  paperWhite: '#FFFEF5',
  paperCream: '#FDF6E3',
} as const;

/**
 * Palette as a descriptive string for prompts.
 */
export const TURN_PALETTE_DESCRIPTION = `
Color palette: cream, soft yellow, warm peach, dusty rose, sage green, sky blue, lavender.
Background: textured off-white/cream paper.
Avoid: bright red, neon colors, pure black, pure white.
`.trim();

// ============================================================================
// Scene Types
// ============================================================================

/**
 * Different scene types that affect composition and mood.
 */
export type TurnSceneType = 
  | 'character_focus'    // Single character or small group
  | 'action_scene'       // Characters doing something
  | 'environment'        // Setting/landscape focus
  | 'object_focus'       // Specific object (food, toy, etc.)
  | 'emotional_moment'   // Character feeling/expression
  | 'cozy_scene'         // Indoor, warm, comforting
  | 'adventure'          // Outdoor, exploring
  | 'abstract'           // For pages with minimal context
  ;

/**
 * Scene-specific style modifiers.
 */
export const SCENE_MODIFIERS: Record<TurnSceneType, string> = {
  character_focus: 'Center composition on the character, simple background, warm spotlight effect.',
  action_scene: 'Dynamic but gentle composition, sense of movement, characters mid-action.',
  environment: 'Wide composition showing the setting, small characters for scale, atmospheric.',
  object_focus: 'Close-up view, object as hero, soft focus background, gentle lighting.',
  emotional_moment: 'Intimate composition, expressive character, muted background, warm tones.',
  cozy_scene: 'Warm interior lighting, comfortable textures, soft shadows, home feeling.',
  adventure: 'Open composition, natural setting, sense of wonder, gentle exploration.',
  abstract: 'Whimsical shapes and patterns, playful composition, gentle gradients.',
};

// ============================================================================
// Prompt Building
// ============================================================================

/**
 * Build a complete Turn-style illustration prompt.
 * 
 * @param sceneDescription - What the illustration should show (from page text or AI analysis)
 * @param sceneType - The type of scene for composition guidance
 * @param additionalContext - Any extra context (e.g., character names, book theme)
 */
export function buildTurnIllustrationPrompt(
  sceneDescription: string,
  sceneType: TurnSceneType = 'character_focus',
  additionalContext?: string
): string {
  const parts: string[] = [];
  
  // 1. NO TEXT instruction FIRST (critical - Gemini weights early content)
  parts.push('Create a purely visual children\'s book illustration. This must be a picture-only image without any text, words, letters, numbers, signs, labels, captions, or speech bubbles anywhere.');
  
  // 2. Scene description
  parts.push(`\nScene: ${sceneDescription}`);
  
  // 3. Additional context if provided
  if (additionalContext) {
    parts.push(`\nContext: ${additionalContext}`);
  }
  
  // 4. Scene-specific modifier
  parts.push(`\nComposition: ${SCENE_MODIFIERS[sceneType]}`);
  
  // 5. Core Turn style
  parts.push(`\nStyle:\n${TURN_STYLE_BASE}`);
  
  // 6. Color palette
  parts.push(`\n${TURN_PALETTE_DESCRIPTION}`);
  
  // 7. Constraints
  parts.push(`\n${TURN_STYLE_CONSTRAINTS}`);
  
  // 8. Final reinforcement
  parts.push(`\n${TURN_STYLE_REINFORCEMENT}`);
  
  return parts.join('\n');
}

/**
 * Analyze text to determine the best scene type.
 * Simple heuristic-based analysis - can be enhanced with LLM later.
 */
export function inferSceneType(text: string): TurnSceneType {
  const lowerText = text.toLowerCase();
  
  // Action indicators
  if (/\b(run|jump|climb|play|chase|catch|throw|dance|swim)\b/.test(lowerText)) {
    return 'action_scene';
  }
  
  // Emotional indicators
  if (/\b(sad|happy|scared|excited|angry|love|hug|cry|laugh|smile)\b/.test(lowerText)) {
    return 'emotional_moment';
  }
  
  // Environment indicators
  if (/\b(forest|ocean|mountain|city|village|sky|field|garden)\b/.test(lowerText)) {
    return 'environment';
  }
  
  // Cozy indicators
  if (/\b(home|bed|kitchen|room|house|cozy|warm|sleep|eat)\b/.test(lowerText)) {
    return 'cozy_scene';
  }
  
  // Adventure indicators
  if (/\b(adventure|explore|discover|journey|travel|find|search)\b/.test(lowerText)) {
    return 'adventure';
  }
  
  // Object focus (specific nouns)
  if (/\b(cake|ball|toy|book|car|gift|food|flower)\b/.test(lowerText)) {
    return 'object_focus';
  }
  
  // Default to character focus
  return 'character_focus';
}

// ============================================================================
// Exports
// ============================================================================

export default {
  TURN_STYLE_BASE,
  TURN_STYLE_CONSTRAINTS,
  TURN_STYLE_REINFORCEMENT,
  TURN_COLOR_PALETTE,
  TURN_PALETTE_DESCRIPTION,
  SCENE_MODIFIERS,
  buildTurnIllustrationPrompt,
  inferSceneType,
};
